import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MarkitAPI } from "@markit/common.api";
import { NotificationType } from "@markit/common.types";
import { API } from "../API";
import { getAccountState } from "../redux/slices/accountSlice";
import { showNotificationBanner } from "../utils/notificationUtils";
import { getApiErrorMessage } from "../utils/apiErrorUtils";

/** Loads the account's connected AI apps and AI plan access. */
const useAiConnections = () => {
  const { accountData } = useSelector(getAccountState).account;
  const [data, setData] =
    useState<MarkitAPI.AiConnections.Get["response"]>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  // A ref, not `data`, so `refresh` keeps a stable identity across loads.
  const hasDataRef = useRef(false);

  // A silent load updates the data without the loading state or an error
  // toast, for refreshes the user didn't ask for.
  const load = useCallback(
    async (silent: boolean) => {
      if (!silent) {
        setLoading(true);
      }
      try {
        setData(await API.aiConnections.getAiConnections({}));
        hasDataRef.current = true;
        setError("");
      } catch (err) {
        if (silent) {
          return;
        }
        const message = getApiErrorMessage(err);
        setError(message);
        // The inline error only shows before the first load, so surface a
        // failed refresh of an already-loaded list as a toast.
        if (hasDataRef.current) {
          showNotificationBanner(dispatch, message, NotificationType.NEGATIVE);
        }
      }
      if (!silent) {
        setLoading(false);
      }
    },
    [dispatch]
  );

  const refresh = useCallback(() => load(false), [load]);

  // Reloads when admin mode switches to another account (typedAPI already
  // sends that account's uid), and when the subscription changes so plan
  // access updates right after an upgrade or a billing fix.
  useEffect(() => {
    refresh();
  }, [
    refresh,
    accountData.uid,
    accountData.customer.phone,
    accountData.customer.state,
  ]);

  // Connecting an AI app happens in another window, so reload when the user
  // comes back to this one and the new app shows up without a page refresh.
  useEffect(() => {
    const reloadOnFocus = () => {
      load(true);
    };
    window.addEventListener("focus", reloadOnFocus);
    return () => window.removeEventListener("focus", reloadOnFocus);
  }, [load]);

  return { data, loading, error, refresh };
};

export default useAiConnections;
